import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tabs, Tab as MuiTab, Badge, Typography, Stack, Box, Button, Grid, IconButton, Collapse } from '@mui/material';
import styled from '@emotion/styled';
import { size } from 'lodash';
import { FocusVersionScheduling, ScheduleAvailType, ScheduleAvailRights } from '@/redux/slices/slice_types';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectAvailTerritory } from '@/redux/selectors/constantsSelectors';
import { axios, capitalizeNoun } from '@/_utils/utils';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { dubRights, subRights, territoryGrouping } from '@/components/Focus/constants';
import ScheduleDetailsGridTile from '@/components/Focus/AvailsScheduleDetails/ScheduleDetailsGridTile';
import AvailEnumMap from '@/components/Focus/Avails/AvailsEnumMap';
import { getColorByElementStatus, getTileBgColorByElStatus, getTileIconBgColorByElStatus } from '@/components/_common/Table/Calendar/utils';
import { FocusTheme } from '@/components/Focus/configs/theme';
import { useFocusTheme } from '@/components/Focus/configs/useFocusTheme.hook';
import Tooltip from '@/components/_common/Tooltip';
import NewScheduleAvailSelectionModal, {
  NewScheduleAvailSelectionModalProps
} from '../AvailsSchedule/NewSchedule/NewScheduleAvails/NewScheduleAvailSelectionModal';
import { selectFocusNewScheduleSlice } from '@/redux/selectors/focusNewScheduleSelectors';
import { getFocusRightsPossibleBySchedulingId, setRightsTaskBySchedulingId } from '@/redux/slices/focusNewScheduleSlice';
import { useToast } from '@/components/_common/Toast';

interface Props {
  schedule: FocusVersionScheduling;
  saveSchedule: (schedule: Partial<FocusVersionScheduling>) => void;
}

enum TabNames {
  Territories = 'territories',
  Audio = 'audio',
  TimedText = 'timed-text'
}

const tabsList = [
  {
    name: TabNames.Territories,
    label: 'Territories'
  },
  {
    name: TabNames.Audio,
    label: 'Audio'
  },
  {
    name: TabNames.TimedText,
    label: 'Timed Text'
  }
];

const regionNamesInEnglish = new Intl.DisplayNames(['en'], { type: 'region' });
const formatTerritoryLabel = (code: string) => {
  const upper = code.toUpperCase();
  return `${regionNamesInEnglish.of(upper) ?? code} [${upper}]`;
};

const ScheduleDetailsTabs: React.FC<Props> = ({ schedule, saveSchedule }) => {
  const dispatch = useAppDispatch();
  const theme: FocusTheme = useFocusTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { error } = useToast();
  const availTerritory = useAppSelector(selectAvailTerritory);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [isSelectionModalOpen, setIsSelectionModalOpen] = useState(false);
  const [scrollToTerritoriesGroupName, setScrollToTerritoriesGroupName] = useState<string | null>(null);
  const [fetchingRights, setFetchingRights] = useState(false);
  const { rightsTaskBySchedulingId, rightsPossibleBySchedulingId } = useAppSelector(selectFocusNewScheduleSlice);
  const selectedTab = tabsList[selectedTabIndex];

  let interval: undefined | NodeJS.Timeout = undefined;

  const rightsPossible = useMemo(() => rightsPossibleBySchedulingId[schedule.id], [rightsPossibleBySchedulingId[schedule.id]]);
  const rightsTask = useMemo(
    () => rightsTaskBySchedulingId[schedule.id] || { status: 'none', id: null },
    [rightsTaskBySchedulingId[schedule.id], schedule.id]
  );

  const selectionModalInitialTab = useMemo((): ScheduleAvailType => {
    switch (selectedTab.name) {
      case TabNames.Territories:
        return 'territories';
      case TabNames.Audio:
        return 'audio';
      case TabNames.TimedText:
        return 'timedtext';
    }
  }, [selectedTab]);

  const territories = schedule.territories;

  const territoriesGrouped = useMemo(() => {
    const territoryToGroupNameMap = Object.entries(territoryGrouping).reduce<Record<string, string>>((acc, [groupName, territoriesList]) => {
      territoriesList.forEach((t) => (acc[t.code.toLowerCase()] = groupName));
      return acc;
    }, {});

    return territories.reduce<Record<string, string[]>>((acc, territory) => {
      if (territoryToGroupNameMap[territory]) {
        if (!acc[territoryToGroupNameMap[territory]]) {
          acc[territoryToGroupNameMap[territory]] = [territory];
        } else {
          acc[territoryToGroupNameMap[territory]].push(territory);
        }
      }
      return acc;
    }, {});
  }, [territories]);

  const [expandedTerritories, setExpandedTerritories] = useState(
    Object.keys(territoriesGrouped).reduce<Record<string, boolean>>((acc, groupName) => {
      acc[groupName] = false;
      return acc;
    }, {})
  );

  const audioElements = useMemo(() => {
    return schedule.elements.filter((el) => el.element.element_type === 'audio');
  }, [schedule]);

  const timedTextElements = useMemo(() => {
    return schedule.elements.filter((el) => el.element.element_type === 'timedtext');
  }, [schedule]);

  const modalInitialSelectedByTab = useMemo(
    (): NewScheduleAvailSelectionModalProps['initialSelectedByTab'] => ({
      territories,
      audio: audioElements.map((el) => el.element.language),
      timedtext: timedTextElements.map((el) => el.element.language)
    }),
    [territories, audioElements, timedTextElements]
  );

  const countByTabName = useMemo(
    () => ({
      [TabNames.Territories]: territories.length,
      [TabNames.Audio]: audioElements.length,
      [TabNames.TimedText]: timedTextElements.length
    }),
    [territories.length, audioElements.length, timedTextElements.length]
  );

  const totalCountByTabName = useMemo(
    () => ({
      [TabNames.Territories]: size(availTerritory),
      [TabNames.Audio]: dubRights.length,
      [TabNames.TimedText]: subRights.length
    }),
    [territories.length, audioElements.length, timedTextElements.length]
  );

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setSelectedTabIndex(newValue);
  };

  const openSelectionModal = useCallback(() => {
    setIsSelectionModalOpen(true);
    fetchRights();
  }, []);

  const handleSelectionModalClose = useCallback(() => {
    setIsSelectionModalOpen(false);
    if (interval) clearInterval(interval);
  }, []);

  const handleModalTransitionExited = useCallback(() => {
    // We reset `scrollToTerritoriesGroupName` after modal disappears (and not inside `handleSelectionModalClose` callback)
    // on purpose. `scrollToTerritoriesGroupName` used to calculate `defaultExpanded` prop value for an Accordion component
    // inside modal. When we reset the `scrollToTerritoriesGroupName` value before the modal actually closed, it changes
    // `defaultExpanded` prop value, which causes annoying MUI warning in the console.
    setScrollToTerritoriesGroupName(null);
  }, []);

  const handleScheduleSave = useCallback(
    (modalData: ScheduleAvailRights) => {
      const updatedSchedule: Partial<FocusVersionScheduling> = {
        territories: modalData.territories.map((t) => t.toLowerCase()),
        allowed_languages: [...modalData.audio.map((a) => `${a}:dub`), ...modalData.timedtext.map((tt) => `${tt}:sub`)]
      };
      saveSchedule(updatedSchedule);
    },
    [saveSchedule, schedule]
  );

  const toggleTerritoriesList = (groupName: string) => {
    setExpandedTerritories((state) => ({
      ...state,
      [groupName]: !state[groupName]
    }));
  };

  const handleAddRemoveTerritoryButtonClick = (groupName: string) => {
    setIsSelectionModalOpen(true);
    setScrollToTerritoriesGroupName(groupName);
  };

  const getPossible = () => {
    setFetchingRights(false);
    dispatch(
      getFocusRightsPossibleBySchedulingId({
        schedulingId: schedule.id,
        title_id: [schedule.title.id],
        license: [schedule.license_type],
        availed_platforms: [schedule.availed_platform],
        term_start__gte: schedule.term_start,
        term_end__lt: schedule.term_end
      })
    );
  };

  const fetchRights = () => {
    setFetchingRights(true);
    axios
      .post('/focus/rights/tasks/create/', { title_id: [schedule.title.id] }, { baseURL: '/api/v1.1' })
      .then((resp) => {
        if (resp.data?.success && resp.data.data?.task_id) {
          dispatch(setRightsTaskBySchedulingId({ status: 'pending', id: resp.data.data?.task_id, schedulingId: schedule.id }));
        } else {
          dispatch(setRightsTaskBySchedulingId({ status: 'completed', id: null, schedulingId: schedule.id }));
        }
      })
      .catch(() => {
        dispatch(setRightsTaskBySchedulingId({ status: 'completed', id: null, schedulingId: schedule.id }));
        getPossible();
      });
  };

  useEffect(() => {
    if (rightsTask.status !== 'completed' && rightsTask.id && fetchingRights) {
      let itterator = 0;
      interval = setInterval(() => {
        itterator++;
        if (itterator >= 15) {
          clearInterval(interval);
          getPossible();
          error('Rights availability check is taking longer than expected. Rights may be outdated.');
        }
        axios.get(`/focus/rights/tasks/${rightsTask.id}/`, { baseURL: '/api/v1.1' }).then((resp) => {
          if (resp.data?.completed_at) {
            dispatch(setRightsTaskBySchedulingId({ status: 'completed', id: rightsTask.id, schedulingId: schedule.id }));
            clearInterval(interval);
            getPossible();
          }
          if (resp.data?.errored_at) {
            clearInterval(interval);
            error('An error occurred while processing the rights task.');
          }
        });
      }, 2000);
      return () => {
        if (interval) clearInterval(interval);
      };
    }
  }, [rightsTask]);

  return (
    <>
      <Tabs
        value={selectedTabIndex}
        sx={{ mb: 3, minHeight: 'auto', borderBottom: `1px solid ${theme.palette.neutral[70]}`, '& .MuiTabs-flexContainer': { gap: 3 } }}
        onChange={handleTabChange}
      >
        {tabsList.map(({ name, label }) => (
          <Tab key={name} label={label} badgeCount={countByTabName[name]} />
        ))}
      </Tabs>

      <TabContent>
        <Stack mb={3} direction="row" gap={2} alignItems="center">
          <Typography component="h2" sx={{ fontSize: '2rem', lineHeight: '2.5rem', fontWeight: 700 }}>
            {selectedTab.label}
          </Typography>
          <Typography sx={{ flexGrow: 1, mt: 0.75, fontSize: '1.143rem', lineHeight: '1.286rem', color: theme.palette.neutral[30] }}>
            ({countByTabName[selectedTab.name]} selected of {totalCountByTabName[selectedTab.name]} rights available)
          </Typography>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<PolarisIcon icon="edit_outlined" width={16} height={16} />}
            onClick={openSelectionModal}
          >
            Edit
          </Button>
        </Stack>

        <Grid container spacing={1.5}>
          {selectedTab.name === TabNames.Territories &&
            Object.entries(territoriesGrouped).map(([groupName, territoriesList]) => (
              <ScheduleDetailsGridTile
                key={groupName}
                primaryText={
                  <Tooltip title={expandedTerritories[groupName] ? undefined : groupName} placement="top">
                    <TerritoryGroupName ellipsis={!expandedTerritories[groupName]}>{groupName}</TerritoryGroupName>
                  </Tooltip>
                }
                secondaryText={`${territoriesList.length} territories added`}
                iconName="map"
                endSlot={
                  <Tooltip title="Edit territories">
                    <AddIconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddRemoveTerritoryButtonClick(groupName);
                      }}
                    >
                      <PolarisIcon icon="add" width={20} height={20} />
                    </AddIconButton>
                  </Tooltip>
                }
                onHeaderClick={() => toggleTerritoriesList(groupName)}
              >
                <Collapse in={expandedTerritories[groupName]} timeout={300} sx={{ flexGrow: 1 }}>
                  <TerritoriesList>
                    <TerritoriesListHeader direction="row">
                      <TerritoriesListHeaderText>Selected Territories</TerritoriesListHeaderText>
                      <Tooltip title="Hide">
                        <IconButton onClick={() => toggleTerritoriesList(groupName)}>
                          <PolarisIcon icon="close" width={16} height={16} />
                        </IconButton>
                      </Tooltip>
                    </TerritoriesListHeader>
                    {territoriesList.map((territory) => (
                      <TerritoryItem key={territory}>{formatTerritoryLabel(territory)}</TerritoryItem>
                    ))}
                  </TerritoriesList>
                </Collapse>
              </ScheduleDetailsGridTile>
            ))}

          {selectedTab.name === TabNames.Audio &&
            audioElements.map(({ element: { language, element_subtype }, inventory_status }, index) => (
              <ScheduleDetailsGridTile
                key={index}
                primaryText={
                  <>
                    <AvailEnumMap type="language" value={language} /> [{language}]
                  </>
                }
                secondaryText={capitalizeNoun(element_subtype)}
                iconName="music"
                color={`${getColorByElementStatus(inventory_status)}.main`}
                iconBgColor={getTileIconBgColorByElStatus(inventory_status, isDarkMode)}
                bgColor={getTileBgColorByElStatus(inventory_status, isDarkMode)}
              />
            ))}

          {selectedTab.name === TabNames.TimedText &&
            timedTextElements.map(({ element: { language, element_subtype }, inventory_status }, index) => (
              <ScheduleDetailsGridTile
                key={index}
                primaryText={
                  <>
                    <AvailEnumMap type="language" value={language} /> [{language}]
                  </>
                }
                secondaryText={capitalizeNoun(element_subtype)}
                iconName="subtitles"
                color={`${getColorByElementStatus(inventory_status)}.main`}
                iconBgColor={getTileIconBgColorByElStatus(inventory_status, isDarkMode)}
                bgColor={getTileBgColorByElStatus(inventory_status, isDarkMode)}
              />
            ))}
        </Grid>
      </TabContent>

      <NewScheduleAvailSelectionModal
        open={isSelectionModalOpen}
        onClose={handleSelectionModalClose}
        onSave={handleScheduleSave}
        initialTab={selectionModalInitialTab}
        initialSelectedByTab={modalInitialSelectedByTab}
        scrollToElementName={scrollToTerritoriesGroupName ?? undefined}
        onTransitionExited={handleModalTransitionExited}
        rightsPossible={rightsPossible}
      />
    </>
  );
};

export default ScheduleDetailsTabs;

const TabContent = styled(Box)`
  padding: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => `${theme.shape.borderRadius * 2.4}px`};
  background-color: ${({ theme }) => (theme as FocusTheme).palette.neutral[100]};
`;

const Tab = ({ label, badgeCount, ...props }: { label: string; badgeCount: number }) => {
  const theme: FocusTheme = useFocusTheme();

  return (
    <MuiTab
      sx={{
        px: 1,
        py: 0.75,
        minHeight: 'auto',
        color: theme.palette.neutral[40],

        '&.Mui-selected': {
          color: theme.palette.neutral[0]
        }
      }}
      label={
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Typography
            sx={{
              textTransform: 'none',
              lineHeight: '1.571rem',
              fontWeight: 600
            }}
          >
            {label}
          </Typography>
          <Badge
            badgeContent={badgeCount}
            color="primary"
            sx={{
              '.MuiBadge-badge': {
                transform: 'none',
                position: 'static',
                height: 16
              }
            }}
          />
        </Stack>
      }
      {...props}
    />
  );
};

const AddIconButton = styled(IconButton)`
  padding: ${({ theme }) => theme.spacing(0.5)};
  background-color: rgba(255, 255, 255, 0.05);

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const TerritoriesList = styled.div`
  margin-top: ${({ theme }) => theme.spacing(0.75)};
  padding: ${({ theme }) => theme.spacing(0.75)};
  background-color: ${({ theme }) => (theme as FocusTheme).palette.neutral[70]};
  border-radius: ${({ theme }) => `${theme.shape.borderRadius * 1}px`};
`;

const TerritoriesListHeader = styled(Stack)`
  align-items: center;
  margin-left: ${({ theme }) => theme.spacing(1)};
`;

const TerritoriesListHeaderText = styled(Typography)`
  flex-grow: 1;
  font-weight: 700;
  color: ${({ theme }) => (theme as FocusTheme).palette.neutral[20]};
`;

const TerritoryItem = styled(Box)`
  padding: ${({ theme }) => theme.spacing(0.5, 1)};
`;

const TerritoryGroupName = styled.span<{ ellipsis: boolean }>`
  display: block;

  ${({ ellipsis }) =>
    ellipsis
      ? `
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      `
      : undefined};
`;
